import {
  type ReactElement,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
  useState,
  useEffect,
  createContext,
  useContext,
} from 'react'
import { useRouter } from 'next/router'
import { Grid, Button } from '@mui/material'
import type { MetaTransactionData } from '@safe-global/safe-core-sdk-types' //TransactionOptions
import PendingTxListener from '@/components/dashboard/SendAssets/PendingTxListener'
import SendingComponent from '@/components/dashboard/SendAssets/SendingComponent'
import { SafeTxContext } from '@/components/tx-flow/SafeTxProvider'
// import { createTx } from '@/services/tx/tx-sender'
// import { useAdvancedParams } from '@/components/tx/AdvancedParams'
import { useTxActions } from '@/components/tx/SignOrExecuteForm/hooks'
import { asError } from '@/services/exceptions/utils'
import { isWalletRejection } from '@/utils/wallets'
import { PendingStatus } from '@/store/pendingTxsSlice'

import { useWeb3 } from '@/hooks/wallets/web3'
import { useCurrentChain } from '@/hooks/useChains'

import {
  newInheritWallet,
  inheritAssets,
  // getInitCode,
  // calculateInheritWalletAddress,
  // entryPointSC,
} from '@/utils/inherit-manager'
import { txDispatch, TxEvent } from '@/services/tx/txEvents'
import { didRevert } from '@/utils/ethers-utils'
import { checkSafeCreated } from '@/components/new-safe/create/logic/index'
import { getRedirect } from '@/components/new-safe/create/logic'
import { useAppDispatch } from '@/store'
import { upsertAddressBookEntry } from '@/store/addressBookV2Slice'
import useSafeInfo from '@/hooks/useSafeInfo'
import { formatError } from '@/utils/formatters'

import { INHERIT_EVENTS, trackEvent } from '@/services/analytics'
import use4337Pack from '@/hooks/use4337Pack'
import FeeSection from '@/components/common/FeeSection'

// import { ModalButtonContext } from '@/components/common/ModalButton'

// import useGasLimit from '@/hooks/useGasLimit'
// import { useCurrentChain } from '@/hooks/useChains'

export const SendProcessingContext = createContext<{
  isSuccess: boolean
  isSending: boolean
  sendError: string
  txStatus: PendingStatus | undefined
  setIsSending: Dispatch<SetStateAction<boolean>>
  setSendError: Dispatch<SetStateAction<string>>
  sendTx: (metaTxData: MetaTransactionData, estimatedFee?: boolean) => Promise<void>
  createSafe: (safeName: string) => Promise<void>
  claimInheritances: (inheritWallet: string, tokens: string[]) => Promise<void>
  txHash: string | undefined
}>({
  isSuccess: false,
  isSending: false,
  txStatus: undefined,
  sendError: '',
  setIsSending: () => {},
  setSendError: () => {},
  sendTx: async () => {},
  createSafe: async () => {},
  claimInheritances: async () => {},
  txHash: undefined,
})

const SendProcessing = ({ children }: { children: ReactNode }): ReactElement => {
  // const { setModalOpen } = useContext(ModalButtonContext)

  const [isSending, setIsSending] = useState(false)
  const [txHash, setTxHash] = useState<string | undefined>(undefined)
  const [userOperationHash, setUserOperationHash] = useState<string | undefined>(undefined)
  const [txId, setTxId] = useState<string | undefined>(undefined)
  const [txStatus, setTxStatus] = useState<PendingStatus | undefined>(undefined)
  const [sendError, setSendError] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [isEstimatedFee, setIsEstimatedFee] = useState(false)
  const [metaTxData, setMetaTxData] = useState<MetaTransactionData | undefined>(undefined)
  const [feeError, setFeeError] = useState('')
  const [fee, setFee] = useState('')

  const { setSafeTx, setSafeTxError } = useContext(SafeTxContext)

  const { safeAddress } = useSafeInfo()

  const { executeTx } = useTxActions()
  const dispatch = useAppDispatch()

  const provider = useWeb3()
  const chain = useCurrentChain()
  const router = useRouter()
  const erc4337Pack = use4337Pack()

  // const { gasLimit, gasLimitError } = useGasLimit(safeTx)
  // const [advancedParams, setAdvancedParams] = useAdvancedParams(gasLimit)
  // const chainInfo = useCurrentChain()

  useEffect(() => {
    if (txStatus && txStatus === PendingStatus.INDEXING) {
      trackEvent({ ...INHERIT_EVENTS.SEND_TX_SUCCESS, ...{ label: txHash } })
      setIsSuccess(true)
      // setIsSending(false)
      return
    }
  }, [txHash, txStatus, sendError])

  const handleFeeError = (error: string) => {
    setFeeError(error)
  }

  const handleFeeChange = ({ feeType, feeAmount }: { feeType: string; feeAmount: string }) => {
    setFee(feeAmount)
  }

  const handleConfirmAfterFee = () => {
    setIsEstimatedFee(false)
    sendTx(metaTxData as MetaTransactionData)
  }

  const handleCloseAfterFee = () => {
    setIsEstimatedFee(false)
    setIsSending(false)
  }

  const sendTx = async (metaTxData: MetaTransactionData, estimatedFee: boolean = false) => {
    // console.log('sendTx', metaTxData, estimatedFee)
    setIsSending(true)
    setTxHash(undefined)
    setTxId(undefined)
    setTxStatus(undefined)
    setIsSuccess(false)
    setSendError('')

    if (estimatedFee) {
      setIsEstimatedFee(estimatedFee)
      setMetaTxData(metaTxData)
      return
    }

    if (!erc4337Pack) {
      setSafeTxError(new Error('No ERC4337 provider found'))
      return
    }

    const safeOperation = await erc4337Pack.createTransaction({ transactions: [metaTxData] })
    const signedSafeOperation = await erc4337Pack.signSafeOperation(safeOperation)
    const userOperationHash = await erc4337Pack.executeTransaction({
      executable: signedSafeOperation,
    })

    const eventParams = { txId: `txId_${userOperationHash}` }
    setUserOperationHash(userOperationHash)
    txDispatch(TxEvent.PROCESSING, { ...eventParams, txHash: userOperationHash })

    let userOperationReceipt = null
    while (!userOperationReceipt) {
      // Wait 2 seconds before checking the status again
      await new Promise((resolve) => setTimeout(resolve, 2000))
      userOperationReceipt = await erc4337Pack.getUserOperationReceipt(userOperationHash)
    }

    // console.log('User operation receipt:', userOperationReceipt)

    if (userOperationReceipt.receipt.transactionHash !== '') {
      setTxHash(userOperationReceipt.receipt.transactionHash)
      txDispatch(TxEvent.PROCESSED, { ...eventParams, safeAddress: safeAddress })
      setTxStatus(PendingStatus.INDEXING)
    }

    // createTx(metaTxData)
    //   .then(async (_safeTx) => {
    //     setSafeTx(_safeTx)
    //     const txOptions = {} as TransactionOptions
    //     try {
    //       const executedTxId = await executeTx(txOptions, _safeTx)
    //       setTxId(executedTxId)
    //     } catch (_err) {
    //       const err = asError(_err)
    //       if (isWalletRejection(err)) {
    //         trackEvent({ ...INHERIT_EVENTS.SEND_TX_FAILED, ...{ label: 'User rejected the transaction' } })
    //         setSendError('User rejected the transaction')
    //       } else {
    //         const formattedError = formatError(err) || 'Something went wrong in sending the transaction'
    //         trackEvent({ ...INHERIT_EVENTS.SEND_TX_FAILED, ...{ label: formattedError } })
    //         setSendError(formattedError)
    //       }
    //       setIsSending(false)
    //       return
    //     }
    //   })
    //   .catch(setSafeTxError)
  }

  const renderFooter = () => {
    return (
      <Grid item xs={12} mt="16px">
        <Grid container>
          <Grid item xs={5} sm={4}>
            <Button
              variant="text"
              sx={{
                paddingY: '10px',
                width: '100%',
                borderRadius: '4px',
                background: 'var(--Ebony-Clay-950, #2F354B)',
                color: 'var(--Ebony-Clay-300, #D2D7E5)',
                ':hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                },
              }}
              onClick={handleCloseAfterFee}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={2} sm={4}></Grid>
          <Grid item xs={5} sm={4} textAlign="right">
            <Button
              disabled={fee === '' || feeError !== ''}
              variant="contained"
              sx={{
                paddingY: '10px',
                width: '100%',
              }}
              onClick={handleConfirmAfterFee}
            >
              Confirm
            </Button>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const handleAllDone = () => {
    setIsSending(false)
    setTxHash(undefined)
    setTxId(undefined)
    setTxStatus(undefined)
    setSendError('')
    setIsSuccess(false)
  }

  const createSafe = async (safeName: string) => {
    // concat the safe name with the label key
    trackEvent({ ...INHERIT_EVENTS.CREATE_WALLET, ...{ label: safeName } })

    handleAllDone()
    setIsSending(true)

    if (!provider) {
      setSendError('No provider found')
      setIsSending(false)
      return
    }

    if (!chain) {
      setSendError('No chain found')
      setIsSending(false)
      return
    }

    const signer = provider.getSigner(0)
    const owner = await signer.getAddress()

    try {
      // const address = await calculateInheritWalletAddress(owner, chain.chainId)
      // console.log('safe address', address)
      // const initCode = await getInitCode(chain.chainId, owner)
      // console.log('init code', initCode)
      // await entryPointSC(provider, initCode)

      const tx = await newInheritWallet(owner, chain.chainId, provider)

      const eventParams = { txId: `txId_${tx.hash}` }
      setTxHash(tx.hash)
      txDispatch(TxEvent.PROCESSING, { ...eventParams, txHash: tx.hash })

      // monitor the transaction
      const receipt = await tx.wait()

      if (didRevert(receipt)) {
        txDispatch(TxEvent.REVERTED, { ...eventParams, error: new Error('Transaction reverted by EVM') })
        trackEvent({ ...INHERIT_EVENTS.CREATE_WALLET_FAILED, ...{ label: 'Transaction reverted by EVM' } })
        setSendError('Transaction reverted by EVM')
      } else {
        const newSafe = await checkSafeCreated(provider, chain)
        const safeAddress = newSafe.getAddress()

        trackEvent({ ...INHERIT_EVENTS.CREATE_WALLET_SUCCESS, ...{ label: safeAddress.replace('0x', '') } })

        txDispatch(TxEvent.PROCESSED, { ...eventParams, safeAddress })
        dispatch(
          upsertAddressBookEntry({
            chainId: chain.chainId,
            address: safeAddress,
            name: safeName !== '' ? safeName : `Inherit Wallet`,
            email: '',
          }),
        )

        const url = getRedirect(chain.shortName, safeAddress, router.query?.walletViewRedirectURL)
        router.push(url)
      }

      setIsSending(false)
    } catch (_err) {
      const err = asError(_err)

      if (isWalletRejection(err)) {
        trackEvent({ ...INHERIT_EVENTS.CREATE_WALLET_FAILED, ...{ label: 'User rejected the transaction' } })
        setSendError('User rejected the transaction')
      } else {
        const formattedError = formatError(err) || 'Something went wrong in sending the transaction'
        trackEvent({ ...INHERIT_EVENTS.CREATE_WALLET_FAILED, ...{ label: formattedError } })
        setSendError(formattedError)
      }
      setIsSending(false)
      return
    }
  }

  const claimInheritances = async (inheritWallet: string, tokens: string[]) => {
    trackEvent({ ...INHERIT_EVENTS.CLAIM_INHERITANCE, ...{ label: inheritWallet } })
    handleAllDone()
    setIsSending(true)

    if (!provider) {
      setSendError('No provider found')
      setIsSending(false)
      return
    }

    if (!chain) {
      setSendError('No chain found')
      setIsSending(false)
      return
    }

    try {
      const tx = await inheritAssets(provider, chain.chainId, inheritWallet, tokens, safeAddress)

      const eventParams = { txId: `txId_${tx.hash}` }
      setTxHash(tx.hash)
      txDispatch(TxEvent.PROCESSING, { ...eventParams, txHash: tx.hash })

      // monitor the transaction
      const receipt = await tx.wait()

      if (didRevert(receipt)) {
        txDispatch(TxEvent.REVERTED, { ...eventParams, error: new Error('Transaction reverted by EVM') })
        trackEvent({ ...INHERIT_EVENTS.CLAIM_INHERITANCE_FAILED, ...{ label: 'Transaction reverted by EVM' } })
        setSendError('Transaction reverted by EVM')
        setIsSending(false)
      } else {
        txDispatch(TxEvent.PROCESSED, { ...eventParams, safeAddress })
        trackEvent({ ...INHERIT_EVENTS.CLAIM_INHERITANCE_SUCCESS, ...{ label: inheritWallet } })
        setTxStatus(PendingStatus.INDEXING)
      }
    } catch (_err) {
      const err = asError(_err)

      if (isWalletRejection(err)) {
        trackEvent({ ...INHERIT_EVENTS.CLAIM_INHERITANCE_FAILED, ...{ label: 'User rejected the transaction' } })
        setSendError('User rejected the transaction')
      } else {
        const formattedError = formatError(err) || 'Something went wrong in sending the transaction'
        trackEvent({ ...INHERIT_EVENTS.CLAIM_INHERITANCE_FAILED, ...{ label: formattedError } })
        setSendError(formattedError)
      }
      setIsSending(false)
      return
    }
  }

  return (
    <SendProcessingContext.Provider
      value={{
        isSending,
        sendTx,
        setIsSending,
        sendError,
        txStatus,
        createSafe,
        setSendError,
        claimInheritances,
        isSuccess,
        txHash,
      }}
    >
      {isEstimatedFee && (
        <>
          <FeeSection metaTxData={metaTxData} onError={handleFeeError} onFee={handleFeeChange} />
          {renderFooter()}
        </>
      )}
      {!isEstimatedFee && (
        <>
          {txId && <PendingTxListener txId={txId} setTxStatus={setTxStatus} setTxHash={setTxHash} />}
          {isSending && (
            <Grid container>
              <Grid
                item
                container
                sm={12}
                justifyContent="center"
                alignContent="center"
                mt="32px"
                mb="16px"
                py="32px"
                sx={{
                  borderRadius: '6px',
                  border: '1px solid var(--Ebony-Clay-950, #2F354B)',
                  // background: 'var(--Supporting-Dark-BG-700, #272A3E)',
                }}
              >
                <SendingComponent
                  isShow={isSending}
                  txHash={txHash}
                  isDone={isSuccess}
                  userOpHash={userOperationHash}
                />
              </Grid>
              {isSuccess && (
                <Grid item container sm={12} textAlign="right">
                  <Button
                    variant="contained"
                    sx={{
                      paddingY: '10px',
                      width: '100%',
                      marginTop: '16px',
                    }}
                    onClick={handleAllDone}
                  >
                    Ok
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
          <div style={{ display: isSending ? 'none' : 'block' }}>{children}</div>
        </>
      )}
    </SendProcessingContext.Provider>
  )
}
export default SendProcessing
